import type { ReactNode } from 'react';
import { useId } from 'react';

/**
 * Ô nhập có nhãn, dùng chung cho mọi form.
 *
 * Chuyển từ `modules/networks/components/` về đây ở bước 12.2: module `revenue`
 * đã import chéo sang, và chuỗi class `LOP_O` còn bị chép lại ở 5 chỗ khác.
 */
export interface OProps {
  label: string;
  value: string;
  onChange: (giaTri: string) => void;
  type?: 'text' | 'email' | 'password' | 'number' | 'date' | 'color';
  /** Gợi ý cho trình quản lý mật khẩu, ví dụ `current-password`. */
  autoComplete?: string;
  required?: boolean;
  /** Ô chỉ đọc: tắt hẳn thay vì để gõ được rồi âm thầm bỏ giá trị. */
  disabled?: boolean;
  nhieuDong?: boolean;
  /** Số (ô `number`) hoặc khoá ngày `YYYY-MM-DD` (ô `date`). */
  min?: number | string;
  max?: number | string;
  step?: number;
  /**
   * Bàn phím trên điện thoại. `numeric` cho ô chỉ nhận chữ số (mã 2FA 6 số):
   * `type="number"` thì có mũi tên tăng/giảm và cuộn chuột đổi giá trị, không
   * đúng cho một mã.
   */
  inputMode?: 'numeric' | 'text';
  maxLength?: number;
  moTa?: string;
  className?: string;
}

/**
 * Giá trị thật của ô nhập, sau khi dọn trạng thái "nhập dở" của ô ngày.
 *
 * Xoá ngày bằng Backspace (cách người dùng hay làm) để lại ô `type="date"` ở
 * trạng thái lạ: `value` đã là `''` nhưng vẫn còn ô ngày/tháng/năm chưa xoá nên
 * `validity.badInput` là `true`. Lúc đó form **bị chặn submit âm thầm**:
 * `onSubmit` không chạy, không request nào được gửi, và React cũng không thấy
 * gì thay đổi vì state vốn đã là `''` — người dùng chỉ thấy bấm Lưu không có
 * tác dụng. Đã gặp thật hôm 11/10/2026 khi xoá ngày kết thúc hợp đồng
 * RouteNote01 (xem `e2e/08-sua-hop-dong-xoa-ngay-ket-thuc.spec.ts`).
 *
 * Ghi lại `value = ''` xoá hẳn các ô còn sót, nên ô ngày trống thì form hợp lệ
 * và lưu được, còn ô ngày **bắt buộc** thì vẫn bị chặn kèm thông báo của trình
 * duyệt — chặn có lý do, không phải âm thầm.
 */
export function docGiaTriO(
  el: { value: string; validity: { badInput: boolean } },
  type: OProps['type'],
): string {
  if (type === 'date' && el.validity.badInput) el.value = '';
  return el.value;
}

/**
 * Class của ô nhập. Export để form khác (ví dụ `FormNhapCsv`) dùng lại.
 *
 * Bước 2/3: cỡ chữ và bo góc lấy **token** (`text-co-ban`, `radius-o`) thay cho
 * 15px/10px gõ tay — một chỗ sửa cho mọi form.
 */
export const LOP_O =
  'vung-bam w-full rounded-o border border-vien bg-be-mat px-3 text-co-ban text-chu placeholder:text-chu-phu disabled:bg-neutral-nen disabled:text-chu-phu';

export function O({
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  required = false,
  disabled = false,
  nhieuDong = false,
  min,
  max,
  step,
  inputMode,
  maxLength,
  moTa,
  className = '',
}: OProps) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-phu font-medium text-chu-phu">
        {label}
        {required && <span className="text-bad-chu"> *</span>}
      </label>
      {nhieuDong ? (
        <textarea
          id={id}
          value={value}
          required={required}
          disabled={disabled}
          rows={3}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          className={`${LOP_O} py-2`}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          required={required}
          disabled={disabled}
          {...(autoComplete === undefined ? {} : { autoComplete })}
          {...(inputMode === undefined ? {} : { inputMode })}
          {...(maxLength === undefined ? {} : { maxLength })}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            onChange(docGiaTriO(e.target, type));
          }}
          className={LOP_O}
        />
      )}
      {moTa && <p className="mt-1 text-phu text-chu-mo">{moTa}</p>}
    </div>
  );
}

export interface OChonTruongProps {
  label: string;
  value: string;
  onChange: (giaTri: string) => void;
  options: { value: string; label: string }[];
  moTa?: string;
  className?: string;
}

export function OChonTruong({
  label,
  value,
  onChange,
  options,
  moTa,
  className = '',
}: OChonTruongProps) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-phu font-medium text-chu-phu">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className={LOP_O}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {moTa && <p className="mt-1 text-phu text-chu-mo">{moTa}</p>}
    </div>
  );
}

/** Khối thông tin nhỏ dạng "nhãn / giá trị". */
export function OThongTin({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-phu text-chu-mo">{nhan}</dt>
      <dd className="mt-1 text-co-ban font-medium text-chu">{children}</dd>
    </div>
  );
}
